/**
 * Viral share-caption helpers.
 *
 * Goal: short, emotional, frictionless. Reads like a meme account caption,
 * not a marketing post. ONE URL, three hashtags max, no leaked card-side tags.
 */

const CORE_TAGS = ["#MoodCard", "#BuiltDifferent"];

const ROTATING_TAGS = [
    "#NoShortcuts",
    "#SayItOnce",
    "#YourTurn",
    "#SoftLaunch",
    "#MainCharacter",
    "#RemixIt",
    "#NoApologies",
    "#OutLoud",
];

const SHARE_CTAS = [
    "see it best →",
    "open this mood →",
    "tap the vibe →",
    "view in full →",
    "the full vibe →",
];

function stableHash(str) {
    let h = 0;
    if (!str) return 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function pickStable(arr, seed) {
    return arr[stableHash(seed) % arr.length];
}

/**
 * Strip any leaked hashtags and quotes from a line — the LLM sometimes appends
 * personalize tags (#trust #scripture) to the body. Those don't belong in
 * the share caption; we own the hashtag namespace.
 */
function _cleanLine(s) {
    if (!s) return "";
    return s
        .replace(/[“”"']+/g, "")           // smart-quotes
        .replace(/#[A-Za-z0-9_]+/g, "")    // any embedded hashtag
        .replace(/\s+/g, " ")              // collapse whitespace
        .trim()
        .replace(/[.!?…,;:\-—\s]+$/, ""); // trailing punctuation
}

/** Pick ONE emotional line from the card — caption first, then first body line. */
export function shareLine(card) {
    if (!card) return "";
    const c = _cleanLine(card.caption);
    if (c) return c.slice(0, 90);
    const first = (card.body || "").split(/\n/).map(_cleanLine).find(Boolean);
    return (first || "").slice(0, 90);
}

/**
 * Build the SHORT, crisp share caption. ONE URL, max 3 hashtags.
 *   "No apologies for succeeding. Remix yours → moodcard.app/c/win-3x #MoodCard #BuiltDifferent #NoApologies"
 */
export function buildShareCaption({ card, shareUrl }) {
    const seed = `${card?.id || ""}-${card?.style || ""}-${shareUrl || ""}`;
    const line = shareLine(card);
    const cta = pickStable(SHARE_CTAS, seed + "-cta");
    const extraTag = pickStable(ROTATING_TAGS, seed + "-tag");
    const tags = [...CORE_TAGS, extraTag].join(" ");
    const url = shareUrl || "";
    const parts = [
        line && `${line}.`,
        url && `${cta} ${url}`,
        tags,
    ].filter(Boolean);
    return parts.join(" ");
}

/**
 * Build the share TEXT for navigator.share() — same crisp tone but WITHOUT
 * the URL, because Web Share API passes `url` as a separate field and target
 * apps (WhatsApp, IG) append it themselves. Embedding it in text would duplicate.
 */
export function buildShareCaptionText({ card }) {
    const seed = `${card?.id || ""}-${card?.style || ""}`;
    const line = shareLine(card);
    const cta = pickStable(SHARE_CTAS, seed + "-cta");
    const extraTag = pickStable(ROTATING_TAGS, seed + "-tag");
    const tags = [...CORE_TAGS, extraTag].join(" ");
    const parts = [line && `${line}.`, cta, tags].filter(Boolean);
    return parts.join(" ");
}

export function buildSocialNudge({ shareUrl }) {
    const seed = shareUrl || "default";
    const cta = pickStable(SHARE_CTAS, seed + "-cta");
    const extraTag = pickStable(ROTATING_TAGS, seed + "-tag");
    const tags = [...CORE_TAGS, extraTag].join(" ");
    return `${cta} ${shareUrl} ${tags}`.trim();
}
